import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { AgentConnection, AgentConnectionRequest } from "@/types";

export function useAgentConnections(
  agentId: string | undefined,
): UseQueryResult<AgentConnection[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: agentId
      ? queryKeys.subList("agents", agentId, "connections")
      : ["resources", "agents", "_invalid", "connections", "list"],
    queryFn: async () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      const res = await client.get<{ connections: AgentConnection[] }>(
        `/agents/${agentId}/connections`,
      );
      return res.connections;
    },
    enabled: !!agentId && !!session,
  });
}

export function useAgentConnectionRequests(
  agentId: string | undefined,
  status?: "pending" | "accepted" | "rejected",
): UseQueryResult<AgentConnectionRequest[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: agentId
      ? [...queryKeys.subList("agents", agentId, "connection-requests"), status ?? "all"]
      : ["resources", "agents", "_invalid", "connection-requests", "list"],
    queryFn: async () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      const path = status
        ? `/agents/${agentId}/connection-requests?status=${status}`
        : `/agents/${agentId}/connection-requests`;
      const res = await client.get<{ requests: AgentConnectionRequest[] }>(path);
      return res.requests;
    },
    enabled: !!agentId && !!session,
  });
}
